import { useState, useMemo } from 'react';
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getPaginationRowModel,
	type SortingState,
} from '@tanstack/react-table';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TanStackTableView } from '@/components/shared/TanStackTableView';
import type { PersonRow, Status } from '../../../utils/panorama/panoramaPeopleRows';
import { STATUS_ORDER } from '../vacationOverviewPage.definitions';
import { vacationTableColumns } from './vacationTable.columns';
import { VacationOverviewCard } from './VacationOverviewCard';

interface VacationTableProps {
	rows: PersonRow[];
	isLoading?: boolean;
}

const STATUS_SORT_INDEX: Record<Status, number> = {
	acquiring: 0,
	expired: 1,
	'approval-urgent': 2,
	pending: 3,
	'expiring-soon': 4,
	none: 5,
	scheduled: 6,
	'on-vacation': 7,
	taken: 8,
};

export function VacationTable({ rows, isLoading = false }: VacationTableProps) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const isMobile = useMediaQuery('(max-width: 1023px)');

	const sortedRows = useMemo(() => {
		const base = sorting.length === 0
			? [...rows].sort((a, b) => {
				const aStatus = STATUS_SORT_INDEX[a.status];
				const bStatus = STATUS_SORT_INDEX[b.status];
				if (aStatus !== bStatus) return aStatus - bStatus;
				return a.name.localeCompare(b.name);
			})
			: rows;

		return [...base].sort((a, b) => {
			const aNone = a.status === 'none' ? 1 : 0;
			const bNone = b.status === 'none' ? 1 : 0;
			return aNone - bNone;
		});
	}, [rows, sorting]);

	const table = useReactTable({
		data: sortedRows,
		columns: vacationTableColumns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageIndex: 0, pageSize: 20 } },
	});

	if (isLoading) {
		return <div className="p-4 text-center text-sm text-raro-gray-500">Carregando...</div>;
	}

	if (rows.length === 0) {
		return (
			<div className="p-6 text-center text-sm text-raro-gray-500">Nenhuma pessoa com esses filtros.</div>
		);
	}

	const tableRows = table.getRowModel().rows;

	return (
		<div className="space-y-3">
			<p className="text-xs text-raro-gray-500">
				{rows.length} {rows.length === 1 ? 'pessoa' : 'pessoas'}
			</p>

			{isMobile ? (
				<div className="space-y-3">
					{tableRows.map(row => (
						<VacationOverviewCard key={row.original.id} row={row.original} />
					))}
					<div className="pt-4">
						<TanStackTableView table={table} showPaginationOnly />
					</div>
				</div>
			) : (
				<TanStackTableView table={table} cellVerticalAlign="middle" />
			)}
		</div>
	);
}
